import React from "react";
import { max, min } from "d3-array";
import { scaleLinear } from "d3-scale";
import { line, area, curveMonotoneX } from "d3-shape";
import { useSpring, useSpringRef, useChain, animated } from "@react-spring/web";

import { Group } from "./Group";
import { Header } from "./Header";
import { InfoSymbol } from "./InfoSymbol";
import { Footer } from "./Footer";
import { AxisBottom } from "./AxisBottom";
import { AxisGrid } from "./AxisGrid";
import { ChartDatum } from "../Chart";

import { linearRegression } from "../../../utils/regression";
import { germanNumber } from "../../../utils/format";
import { chartColors } from "../../../config/colors";

interface Props {
  id: string;
  data: ChartDatum[];
  title?: string;
  description?: string;
  source?: string;
  sourceLink?: string;
  infoLink?: string;
  unit?: string;
  color?: string;
  xKey?: string;
  yKey?: string;
  width?: number;
  height?: number;
  isAnimated?: boolean;
}

export const LineChart: React.FC<Props> = ({
  id,
  data,
  title,
  description,
  source,
  sourceLink,
  infoLink,
  unit,
  color = chartColors.linePrimary,
  xKey = "year",
  yKey = "value",
  width = 210,
  height = 160,
  isAnimated = true,
}) => {
  const margin = { top: 70, right: 0, bottom: 40, left: 0 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xMin = min(data, (d) => d[xKey])!;
  const xMax = max(data, (d) => d[xKey])!;
  const x = scaleLinear().domain([xMin, xMax]).range([0, innerWidth]);
  const xTicks = x.ticks(4);

  const yMin = min(data, (d) => d[yKey])!;
  const yMax = max(data, (d) => d[yKey])!;
  const y = scaleLinear().domain([yMin, yMax]).range([innerHeight, 0]);
  const yTicks = y.ticks(3);

  const regression = linearRegression(data, xKey, yKey);
  const percentChange =
    ((regression(xMax) - regression(xMin)) / Math.abs(regression(xMin))) * 100;
  const endLabel =
    yMin > 0
      ? `+${Math.round(percentChange)} %`
      : `+${Math.round((yMax - yMin) * 10) / 10} ${unit}`;

  const lineConstructor = line<ChartDatum>()
    .x((d) => x(d[xKey]))
    .y((d) => y(d[yKey]))
    .curve(curveMonotoneX);

  const areaConstructor = area<ChartDatum>()
    .x((d) => x(d[xKey])!)
    .y0(() => innerHeight)
    .y1((d) => y(d[yKey]))
    .curve(curveMonotoneX);

  const lineRef = useSpringRef();
  const lineAnimation = useSpring({
    config: {
      duration: isAnimated ? 750 : 0,
    },
    width: isAnimated ? innerWidth : 0,
    ref: lineRef,
  });

  const labelRef = useSpringRef();
  const labelAnimation = useSpring({
    config: {
      duration: isAnimated ? 250 : 0,
    },
    opacity: isAnimated ? 1 : 0,
    ref: labelRef,
  });

  useChain([lineRef, labelRef], [0, 0.75]);

  return (
    <>
      {title && <Header title={title} description={description} />}
      {infoLink && (
        <InfoSymbol
          link={infoLink}
          transform={`translate(${innerWidth - 15}, ${-5})`}
        />
      )}
      {source && (
        <Footer
          text={`Quelle: ${source}`}
          link={sourceLink}
          alignRight={true}
          transform={`translate(${width}, ${height})`}
        />
      )}
      <Group transform={`translate(${margin.left}, ${margin.top})`}>
        <AxisBottom
          scale={x}
          ticks={xTicks}
          showTickMarks={true}
          fill={chartColors.fontSecondary}
          transform={`translate(0, ${innerHeight})`}
        />
        <AxisGrid
          scale={y}
          ticks={yTicks}
          tickUnit={unit}
          tickFormatter={germanNumber}
          tickMarkLength={innerWidth}
          fill={chartColors.fontSecondary}
          stroke={chartColors.lineTertiary}
        />
        <Group>
          <defs>
            <clipPath id={`clip-${id}`}>
              <animated.rect {...lineAnimation} height={innerHeight} />
            </clipPath>
          </defs>
          <path
            clipPath={`url(#clip-${id})`}
            d={lineConstructor(data)!}
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            clipPath={`url(#clip-${id})`}
            d={areaConstructor(data)!}
            stroke="none"
            fill={color}
            fillOpacity="0.5"
          />
        </Group>
        <Group transform={`translate(${x(xMax)}, ${y(yMax)})`}>
          <animated.circle
            cx="0"
            cy="0"
            r="5"
            fill={color}
            {...labelAnimation}
          />
          <animated.text
            fontFamily="TheMixExtraBold, sans-serif"
            fontWeight="700"
            fontSize="14"
            textAnchor="end"
            fill={color}
            stroke="white"
            strokeWidth="3"
            strokeLinejoin="round"
            dy="0.3em"
            dx="-0.75em"
            style={{ paintOrder: "stroke" }}
            {...labelAnimation}
          >
            {endLabel}
          </animated.text>
        </Group>
      </Group>
    </>
  );
};
